<script lang="ts">
import { Component, Vue, Watch } from "vue-property-decorator";
import Sidebar from "../../components/global/Sidebar.vue";
import HeaderBar from "../../components/global/MainHeader.vue";
import CustomerModal from "../../components/global/CustomerModal.vue";
import Loader from "../../components/global/Loader.vue";
import { IDialog } from "@/store/dialog";
import { IAppointmentPatch } from "@/store/modules/appointments/appointmentModule";
import { appointmentsModule } from "@/store/modules/appointments/appointmentModule";
import { formatDate, FormatSpaceType, formatTime } from "../../types/formatDateHelper";
import InvoiceCreator from "../../components/InvoiceCreator.vue";
import InvoicesList from "../../components/InvoicesList.vue";
import AppointmentMenu from "../../components/AppointmentMenu.vue";
import PaymentList from "../../components/PaymentList.vue";
import PaymentCreator from "../../components/global/PaymentCreator.vue";
import GiftCardSaleDialog from "@/components/giftcards/GiftCardSaleDialog.vue";
import GiftCardRedeemDialog from "@/components/giftcards/GiftCardRedeemDialog.vue";
import {
  GiftCardRedemptionChannel,
  GiftCardStatus,
  IGiftCardDTO,
  IAppointmentDTO,
  IAppointmentGiftCardRedemptionDTO,
  IAppointmentInfoDTO,
  IAuditLogDTO,
  IUserDTO,
  ILedgerEntryDTO
} from "@shared/types";
import { formatDateStringHour } from "../../Utilities/dateUtility";
import AppointmentSummary from "@/components/global/AppointmentSummary.vue";
import { dialogModule } from "@/store/modules/dialogModule/dialogModule";
import { userModule } from "@/store/modules/user/userModule";
import { auditLogModule } from "@/store/modules/auditLog/auditLogModule";
import { userPermissionsModule } from "@/store/modules/userPermissionsModule/userPermissionsModule";
import { Action } from "vuex-class";
import { DialogType } from "@/store/modules/dialogModule/dialogModule";
import { ledgerEntriesModule } from "@/store/modules/ledger/ledgerEntriesModule";
import store from "@/store/store";
import CreateLedgerEntryDialog from "@/components/global/CreateLedgerEntryDialog.vue";
import LedgerEntriesOverview from "@/components/global/LedgerEntriesOverview.vue";
import { giftCardsModule } from "@/store/modules/giftCards/giftCardsModule";
import { snackbarModule } from "@/store/modules/snackbar/snackbarModule";
import { AppointmentStatusCode } from "@/services/api/AppointmentService";
import { getApiErrorMessage } from "@/Utilities/apiError";

@Component({
  name: "Appointment",
  components: {
    AppointmentSummary,
    PaymentCreator,
    PaymentList,
    AppointmentMenu,
    InvoicesList,
    InvoiceCreator,
    GiftCardSaleDialog,
    GiftCardRedeemDialog,
    Loader,
    CustomerModal,
    Sidebar,
    HeaderBar,
    CreateLedgerEntryDialog,
  },
})
export default class Appointment extends Vue {
  public appointment: IAppointmentInfoDTO = null;
  public newPayment: boolean = false;
  public movingAppointment: boolean = false;
  public isLoadingPayments: boolean = false;
  public showLedgerEntryDialog: boolean = false;
  public showGiftCardSelection: boolean = false;
  public showGiftCardRedeemDialog: boolean = false;
  public redeemDialogInitialCode: string = "";
  public redeemDialogMaxAmount = 0;

  get user() {
    return userModule.user;
  }

  get canWriteAppointmentsAndClients(): boolean {
    return userPermissionsModule.canWriteAppointmentsAndClients;
  }

  get giftCardFeatureEnabled(): boolean {
    return !!this.user?.useGiftCards;
  }

  get shouldShowGiftCardSection(): boolean {
    if (!this.giftCardFeatureEnabled) {
      return false;
    }
    if (this.appointment?.hasGiftCardRedemptions) {
      return true;
    }
    return this.canWriteAppointmentsAndClients;
  }

  /** Status options: the stored status code is the value, the translated label is the text. */
  get invoiceStatuses(): { text: string; value: AppointmentStatusCode }[] {
    if (this.user.enablePaymentModule) {
      return [
        { text: this.$t(this.$ts.appointment.status.ny).toString(), value: "Ny" },
        { text: this.$t(this.$ts.appointment.status.invoiceCreated).toString(), value: "Faktura opprettet" },
        { text: this.$t(this.$ts.appointment.status.invoiceSent).toString(), value: "Faktura sendt" },
        { text: this.$t(this.$ts.appointment.status.invoicePaid).toString(), value: "Faktura betalt" },
        { text: this.$t(this.$ts.appointment.status.ferdig).toString(), value: "Ferdig" },
      ];
    } else {
      return [
        { text: this.$t(this.$ts.appointment.status.ny).toString(), value: "Ny" },
        { text: this.$t(this.$ts.appointment.status.ferdig).toString(), value: "Ferdig" },
      ];
    }
  }

  getStatusLabel(code: string): string {
    const option = this.invoiceStatuses.find((x) => x.value === code);
    return option ? option.text : code;
  }

  async loadAppointment() {
    console.log("Loading appointment...");
    const params: any = this.$route.params;
    let appointmentNumber: number = params.appointmentNumber;

    try {
      const appointmentResult = await appointmentsModule.getAppointmentByNumber(appointmentNumber);
      console.log("Appointment result: ", appointmentResult);
      this.appointment = appointmentResult;

      //this.invoices = await this.getInvoicesForAppointment(this.appointment.id);
      //this.isLoadingPayments = true;
      //this.payments = await this.getPaymentsForAppointment(this.appointment.id);
      //this.isLoadingPayments = false;
    } catch (e) {
      let dialog: IDialog = {
        text: this.$t(this.$ts.appointment.notFound).toString(),
      };
      await dialogModule.addToDialogQueue(dialog);
      this.goBack();
    }
  }

  async created() {
    await this.loadAppointment();
    const params: any = this.$route.params;
    let appointmentNumber: number = params.appointmentNumber;

    await auditLogModule.postAudit({
      dto: {
        message: " went to appointment page - appoinment: " + appointmentNumber,
        type: 4, // PageVisit
        action: 3, // Info
        comment: "",
        hideForUser: true,
      },
    });
  }

  goBack() {
    appointmentsModule.appointmentSource = "";

    if (appointmentsModule.appointmentSource) {
      this.$router.push("/" + appointmentsModule.appointmentSource).catch(() => {});
    } else {
      this.$router.push("/bestillinger").catch(() => {});
    }
  }

  formatDateCorrectly(day: string | number, month: string | number, year: string | number): string {
    let dateStr = month + "/" + day + "/" + year;
    let datObject = new Date(dateStr);
    return formatDate(datObject, FormatSpaceType.DOT);
  }

  formatTimeCorrectly(hour: string | number, minute: string | number): string {
    if (this.user?.timeFormat === "12h") {
      const hourNum = typeof hour === 'string' ? parseInt(hour) : hour;
      const minuteNum = typeof minute === 'string' ? parseInt(minute) : minute;
      
      const period = hourNum >= 12 ? this.$t(this.$ts.appointmentView.timeFormat.pm) : this.$t(this.$ts.appointmentView.timeFormat.am);
      const hour12 = hourNum % 12 || 12; // Convert 0 to 12 for 12 AM
      
      return `${hour12}:${minuteNum.toString().padStart(2, '0')} ${period}`;
    }
    
    return formatTime(hour, minute);
  }

  async updateAdminComment(event, selectedEvent: IAppointmentInfoDTO) {
    let update: Partial<IAppointmentDTO> = {
      adminNote: selectedEvent.adminNote,
    };

    const payload: IAppointmentPatch = {
      appointmentId: selectedEvent.id,
      patch: update,
    };

    await appointmentsModule.patchAppointment(payload);
  }

  formatDateStringHour(date: Date) {
    return formatDateStringHour(date);
  }

  /** Same colours as AppointmentList.getColor (Ny uses #E0E0E0 here instead of "light"). */
  getColor(status: string): string {
    switch (status) {
      case "Ny":
        return "#E0E0E0";
      case "Faktura opprettet":
        return "orange";
      case "Faktura sendt":
        return "yellow";
      case "Faktura betalt":
        return "green";
      case "Ferdig":
        return "#80cc74";
      default:
        return "grey";
    }
  }

  async onFinished() {
    this.newPayment = false;
    //this.payments = await this.getPaymentsForAppointment(this.appointment.id);
    await this.loadAppointment();
  }

  moveAppointment() {
    this.movingAppointment = true;
  }

  async onMovedAppointment() {
    this.movingAppointment = false;
    await this.loadAppointment();
  }

  async onChangeCustomer() {
    await this.loadAppointment();
  }

  deleteAppointment() {}

  onDeleted() {
    this.goBack();
  }

  get appointmentStart() {
    const chars = "2020-06-07T07:30:00";

    const start = new Date(
      this.appointment.year,
      this.appointment.month - 1,
      this.appointment.day,
      this.appointment.hour,
      this.appointment.minute,
      0
    );
    const tzoffset = new Date().getTimezoneOffset() * 60000; //offset in milliseconds
    //@ts-ignore
    const localISOTime = new Date(start - tzoffset).toISOString().slice(0, -1);

    return localISOTime.substr(0, chars.length);
  }

  formatAppointmentEnd() {
    let start = new Date(
      this.appointment.year,
      this.appointment.month - 1,
      this.appointment.day,
      this.appointment.hour,
      this.appointment.minute,
      0
    );
    //@ts-ignore
    let end = new Date(
      //@ts-ignore
      start.getTime() + this.appointment.appointmentDuration * 1000 * 60
    );
    let h = end.getHours();
    let m = end.getMinutes();
    return this.formatTimeCorrectly(h, m);
  }

  /** Saves the status code through PATCH api/Appointment/current/{id}/status; reverts on failure. */
  async updateInvoiceStatus(newStatus: AppointmentStatusCode, appointment: IAppointmentInfoDTO) {
    if (!appointment || !newStatus || newStatus === appointment.status) {
      return;
    }
    const previousStatus = appointment.status;
    appointment.status = newStatus;
    try {
      const result = await appointmentsModule.changeStatus({ appointmentId: appointment.id, status: newStatus });
      appointment.status = result?.code ?? newStatus;
    } catch (error) {
      console.error("Failed to change appointment status", error);
      appointment.status = previousStatus;
      snackbarModule.postErrorSnackbarMessage(
        getApiErrorMessage(error, this.$t(this.$ts.appointment.statusChangeFailed).toString())
      );
    }
  }

  async markAsUseLedgerEntry() {
    const handleConfirm = async () => {
      try {
        await ledgerEntriesModule.createFromAppointment({
          appointmentId: this.appointment.id,
        });
        await this.loadAppointment();
      } catch (error) {
        await dialogModule.addToDialogQueue({
          header: "Error",
          text: "Failed to create ledger entry. " + error.message,
          type: DialogType.Message,
        });
      }
    };

    const dialog: IDialog = {
      header: String(this.$t(this.$ts.appointment.markAsUnpaid)),
      text: String(this.$t(this.$ts.appointment.markAsUnpaidWarning)),
      type: DialogType.YesNo,
      action: handleConfirm,
      cancelAction: () => {}, // Empty function for cancel
    };

    await dialogModule.addToDialogQueue(dialog);
  }

  onLedgerEntrySave(entry: ILedgerEntryDTO) {
    // TODO: Implement save functionality when needed
    console.log("Ledger entry to save:", entry);
  }

  /** The server only redeems Active cards (a partially used card stays Active). */
  get activeGiftCardStatuses(): GiftCardStatus[] {
    return [GiftCardStatus.Active];
  }

  /** Price minus earlier gift card redemptions on this appointment, never below 0. */
  get remainingToPay(): number {
    const totalPrice = this.appointment?.price ?? this.appointment?.appointmentTypePrice ?? 0;
    const redeemed = (this.appointment?.giftCardRedemptions ?? []).reduce(
      (sum, redemption) => sum + (Number(redemption.amount) || 0),
      0
    );
    // Round to cents so e.g. 500 - 199.99 doesn't become 300.01000000000005.
    return Math.max(Math.round((totalPrice - redeemed) * 100) / 100, 0);
  }

  openGiftCardSelection(): void {
    if (!this.giftCardFeatureEnabled || !this.canWriteAppointmentsAndClients) {
      return;
    }
    this.showGiftCardSelection = true;
  }

  onGiftCardSelection(cards: IGiftCardDTO[]): void {
    if (!this.giftCardFeatureEnabled) {
      return;
    }
    if (!cards || !cards.length) {
      return;
    }
    const [card] = cards;
    if (!card) {
      return;
    }
    this.showGiftCardSelection = false;
    const remaining = this.remainingToPay;
    // The redeem dialog treats maxAmount 0 as "no limit", so don't open it when nothing is left to pay.
    if (remaining <= 0) {
      snackbarModule.postErrorSnackbarMessage(this.$t(this.$ts.giftCardDialogs.nothingLeftToPay).toString());
      return;
    }
    this.redeemDialogInitialCode = card.code;
    this.redeemDialogMaxAmount = remaining;
    this.showGiftCardRedeemDialog = true;
  }

  @Watch("showGiftCardRedeemDialog")
  onRedeemDialogVisibilityChanged(open: boolean) {
    if (!open) {
      this.redeemDialogInitialCode = "";
      this.redeemDialogMaxAmount = 0;
    }
  }

  @Watch("user.useGiftCards")
  onUserGiftCardSettingChanged(enabled: boolean | undefined) {
    if (!enabled) {
      this.showGiftCardSelection = false;
      this.showGiftCardRedeemDialog = false;
    }
  }

  async onGiftCardRedeemConfirm(payload: { code: string; amount: number; notes: string; channel: GiftCardRedemptionChannel }): Promise<void> {
    if (!this.giftCardFeatureEnabled || !this.appointment) {
      return;
    }
    try {
      await giftCardsModule.redeemGiftCard({
        code: payload.code,
        amount: payload.amount,
        notes: payload.notes,
        channel: payload.channel,
        appointmentId: this.appointment.id,
        customerId: this.appointment.customerId && this.appointment.customerId > 0 ? this.appointment.customerId : undefined,
        staffId: this.appointment.staffMemberId && this.appointment.staffMemberId > 0 ? this.appointment.staffMemberId : undefined,
      });
      this.showGiftCardRedeemDialog = false;
      await this.loadAppointment();
    } catch (error) {
      console.error("Failed to redeem gift card for appointment", error);
      snackbarModule.postErrorSnackbarMessage(
        getApiErrorMessage(error, this.$t(this.$ts.giftCardDialogs.redeemFailed).toString())
      );
    }
  }

  giftCardTooltip(redemption: IAppointmentGiftCardRedemptionDTO): string {
    const initial = this.formatCurrency(redemption.initialValue, redemption.currency);
    const remaining = this.formatCurrency(redemption.balanceAfter, redemption.currency);
    const date = this.formatDateStringHour(new Date(redemption.redeemedAt));
    const notesSegment = redemption.notes
      ? `\n${this.$t(this.$ts.appointment.giftCards.notes)}: ${redemption.notes}`
      : "";
    return this.$t(this.$ts.appointment.giftCards.tooltip, {
      initial,
      remaining,
      date,
      notes: notesSegment,
    }).toString();
  }

  goToCustomer() {
    if (this.appointment && this.appointment.customerId && this.appointment.customerId !== 0 && this.appointment.customerId !== -1) {
      this.$router.push(`/kunder/${this.appointment.customerId}`);
    }
  }

  formatCurrency(value: number, currency?: string): string {
    const currencyCode = currency || this.user?.currency || "USD";
    return new Intl.NumberFormat(this.$i18n.locale, {
      style: "currency",
      currency: currencyCode,
    }).format(value);
  }
}
</script>

<template>
  <div>
    <loader v-if="appointment == null" />
    <div v-else>
    <v-app-bar
      class="hidden-lg-and-up"
      color="white"
      fixed
      top
    >
      <v-container fluid>
        <v-row
          fluid
          class="flex-wrap flex-shrink-1"
          justify="space-between"
        >
          <v-col
            cols="auto"
            align="center"
            jusitfy="center"
          >
            <v-btn
              class="mobile-header-button"
              icon
              large
              @click="goBack()"
            >
              <v-icon x-large>
                mdi-chevron-left
              </v-icon>
            </v-btn>
          </v-col>

          <v-spacer />

          <v-col
            cols="auto"
            align="center"
            jusitfy="center"
          >
            <appointment-menu
              :appointment-date="appointmentStart"
              :appointment="appointment"
              :appointment-id="appointment.id"
              :display-show-in-calendar="true"
              :spread="true"
              :large="true"
              @deleted="onDeleted"
              @close="onMovedAppointment"
              @confirmed="onMovedAppointment"
              @changecustomer="onChangeCustomer"
            />
          </v-col>
        </v-row>
      </v-container>
    </v-app-bar>

    <v-row
      justify-md="center"
      class="mt-10"
    >
      <v-col
        xs="12"
        lg="12"
      >
        <v-card class="appointment-card">
          <v-card-title
            style="background-color: #866afe"
            class="white--text"
          >
            <v-icon
              left
              color="white"
            >
              mdi-calendar-check
            </v-icon>
            <span class="headline">{{ $t($ts.entities.appointment) }} #{{ appointment.number }}</span>
            <v-spacer />
            <appointment-menu
              class="hidden-md-and-down"
              :appointment-date="appointmentStart"
              :appointment="appointment"
              :appointment-id="appointment.id"
              :display-show-in-calendar="true"
              :spread="true"
              :large="true"
              color="white"
              @deleted="onDeleted"
              @close="onMovedAppointment"
              @confirmed="onMovedAppointment"
              @changecustomer="onChangeCustomer"
            />
          </v-card-title>

          <v-card-text class="pt-4">
            <v-container fluid>
              <!-- Status Section -->
              <v-row class="mb-4">
                <v-col cols="12">
                  <v-card
                    outlined
                    class="status-card"
                  >
                    <v-card-text>
                      <div class="d-flex align-center">
                        <v-icon
                          style="color: #866afe"
                          class="mr-2"
                        >
                          mdi-flag
                        </v-icon>
                        <div class="w-100">
                          <div class="subtitle-1">
                            {{ $t($ts.appointment.appointmentStatus) }}
                          </div>
                          <v-select
                            v-if="appointment != null && appointment.status != null"
                            :value="appointment.status"
                            :color="getColor(appointment.status)"
                            chips
                            dense
                            hide-details
                            :items="invoiceStatuses"
                            item-text="text"
                            item-value="value"
                            :readonly="!canWriteAppointmentsAndClients"
                            :disabled="!canWriteAppointmentsAndClients"
                            class="mt-1"
                            @change="updateInvoiceStatus($event, appointment)"
                          >
                            <template #selection="data">
                              <v-chip :color="getColor(data.item.value)">
                                <span>{{ getStatusLabel(data.item.value) }}</span>&nbsp;
                              </v-chip>
                            </template>
                          </v-select>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Date and Time Section -->
              <v-row class="mb-4">
                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-card
                    outlined
                    class="time-card"
                  >
                    <v-card-text>
                      <div class="d-flex align-center mb-2">
                        <v-icon
                          style="color: #866afe"
                          class="mr-2"
                        >
                          mdi-calendar
                        </v-icon>
                        <div>
                          <div class="subtitle-1">
                            {{ $t($ts.date) }}
                          </div>
                          <div>{{ formatDateCorrectly(appointment.day, appointment.month, appointment.year) }}</div>
                        </div>
                      </div>
                      <div class="d-flex align-center">
                        <v-icon
                          style="color: #866afe"
                          class="mr-2"
                        >
                          mdi-clock-outline
                        </v-icon>
                        <div>
                          <div class="subtitle-1">
                            {{ $t($ts.appointment.time) }}
                          </div>
                          <div>{{ formatTimeCorrectly(appointment.hour, appointment.minute) }} - {{ formatAppointmentEnd() }}</div>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Customer Section -->
                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-card
                    outlined
                    class="customer-card" 
                    :style="appointment.customerId && appointment.customerId !== 0 && appointment.customerId !== -1 ? 'cursor: pointer;' : ''" 
                    @click="appointment.customerId && appointment.customerId !== 0 && appointment.customerId !== -1 ? goToCustomer() : null"
                  >
                    <v-card-text>
                      <div class="d-flex align-center">
                        <v-icon
                          style="color: #866afe"
                          class="mr-2"
                        >
                          mdi-account
                        </v-icon>
                        <div>
                          <div class="subtitle-1">
                            {{ $t($ts.appointment.customer) }}
                          </div>
                          <div class="d-flex align-center">
                            {{ appointment.customerName }}
                            <v-icon
                              v-if="appointment.customerId && appointment.customerId !== 0 && appointment.customerId !== -1"
                              small
                              class="ml-1"
                              color="#866afe"
                            >
                              mdi-open-in-new
                            </v-icon>
                          </div>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Appointment Type Section -->
              <v-row class="mb-4">
                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-card
                    outlined
                    class="type-card"
                  >
                    <v-card-text>
                      <div class="d-flex align-center">
                        <v-icon
                          style="color: #866afe"
                          class="mr-2"
                        >
                          mdi-tag
                        </v-icon>
                        <div>
                          <div class="subtitle-1">
                            {{ $t($ts.appointment.service) }}
                          </div>
                          <div>{{ appointment.appointmentName }}</div>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Price Section -->
                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-card
                    outlined
                    class="price-card"
                  >
                    <v-card-text>
                      <div class="d-flex align-center">
                        <v-icon
                          style="color: #866afe"
                          class="mr-2"
                        >
                          mdi-cash
                        </v-icon>
                        <div>
                          <div class="subtitle-1">
                            {{ $t($ts.price) }}
                          </div>
                          <div>{{ formatCurrency(appointment.appointmentTypePrice) }}</div>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <v-row
                v-if="shouldShowGiftCardSection"
                class="mb-4"
              >
                <v-col cols="12">
                  <v-card
                    outlined
                    class="gift-card-card"
                  >
                    <v-card-title class="d-flex align-center justify-space-between flex-wrap">
                      <div class="d-flex align-center mb-2 mb-md-0">
                        <v-icon
                          style="color: #866afe"
                          class="mr-2"
                        >
                          mdi-card-text-outline
                        </v-icon>
                        <div>
                          <div class="subtitle-1">
                            {{ $t($ts.appointment.giftCards.title) }}
                          </div>
                          <p class="caption text--secondary mb-0">
                            {{ $t($ts.appointment.giftCards.description) }}
                          </p>
                        </div>
                      </div>
                      <v-btn
                        v-if="canWriteAppointmentsAndClients"
                        small
                        color="primary"
                        class="ml-md-4"
                        @click="openGiftCardSelection"
                      >
                        <v-icon left small>
                          mdi-plus
                        </v-icon>
                        {{ $t($ts.appointment.giftCards.add) }}
                      </v-btn>
                    </v-card-title>
                    <v-card-text>
                      <div v-if="appointment.hasGiftCardRedemptions" class="gift-card-chip-group">
                        <v-tooltip
                          v-for="redemption in appointment.giftCardRedemptions"
                          :key="`${redemption.giftCardId}-${redemption.redeemedAt}`"
                          top
                        >
                          <template #activator="{ on }">
                            <v-chip
                              v-on="on"
                              outlined
                              color="primary"
                              class="mr-2 mb-2"
                            >
                              <v-icon left small>
                                mdi-card-text-outline
                              </v-icon>
                              {{ redemption.giftCardCode }} · {{ formatCurrency(redemption.amount, redemption.currency) }}
                            </v-chip>
                          </template>
                          <span class="gift-card-tooltip">{{ giftCardTooltip(redemption) }}</span>
                        </v-tooltip>
                      </div>
                      <p
                        v-else
                        class="text--secondary caption mb-0"
                      >
                        {{ $t($ts.appointment.giftCards.empty) }}
                      </p>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Notes Section -->
              <v-row
                v-if="appointment.adminNote"
                class="mb-4"
              >
                <v-col cols="12">
                  <v-card
                    outlined
                    class="notes-card"
                  >
                    <v-card-text>
                      <div class="d-flex align-center">
                        <v-icon
                          style="color: #866afe"
                          class="mr-2"
                        >
                          mdi-text
                        </v-icon>
                        <div>
                          <div class="subtitle-1">
                            {{ $t($ts.appointment.adminNote) }}
                          </div>
                          <div>{{ appointment.adminNote }}</div>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Created Section -->
              <v-row v-if="!appointment.isCustom && appointment.created != null">
                <v-col cols="12">
                  <v-card
                    outlined
                    class="created-card"
                  >
                    <v-card-text>
                      <div class="d-flex align-center">
                        <v-icon
                          style="color: #866afe"
                          class="mr-2"
                        >
                          mdi-clock-outline
                        </v-icon>
                        <div>
                          <div class="subtitle-1">
                            {{ $t($ts.appointment.created) }}
                          </div>
                          <div>{{ formatDateStringHour(appointment.created) }}</div>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Custom Event Section -->
              <v-row v-if="appointment.isCustom">
                <v-col cols="12">
                  <v-card
                    outlined
                    class="event-card"
                  >
                    <v-card-text>
                      <div class="d-flex align-center">
                        <v-icon
                          style="color: #866afe"
                          class="mr-2"
                        >
                          mdi-star
                        </v-icon>
                        <div>
                          <div class="subtitle-1">
                            {{ $t($ts.event) }}
                          </div>
                          <div>{{ appointment.appointmentName }}</div>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="false">
      <!-- <v-col>
        <v-card>
          <LedgerEntriesOverview
            :appointmentId="appointment.id"
            :useLedgerEntry="appointment.useLedgerEntry"
            :customer-name="appointment.customerName"
            :customer-email="appointment.customerEmail"
            :appointment-number="appointment.number.toString()"
            :customer-id="appointment.customerId"
          />
        </v-card>
      </v-col> -->
      <!-- <v-col v-if="user.enablePaymentModule">
        <v-card>
          <v-card-title>{{ $t("payment") }}</v-card-title>

          <v-btn right fab color="success" @click="newPayment = !newPayment"><v-icon>mdi-plus</v-icon></v-btn>
          <v-scroll-y-transition>
            <div class="mt-5">
              <payment-creator
                v-if="newPayment"
                :defaultValue="appointment.appointmentTypePrice"
                :appointment="appointment"
                @finished="onFinished"
              ></payment-creator>
            </div>
          </v-scroll-y-transition>

           <payment-list
            :isLoading="isLoadingPayments"
            @invoice="onFinished"
            :showAllInfo="false"
            :payments="payments"
            class="mt-5"
          ></payment-list>
        </v-card>
      </v-col>  -->
    </v-row>
  </div>

  <gift-card-sale-dialog
    v-if="giftCardFeatureEnabled && canWriteAppointmentsAndClients"
    v-model="showGiftCardSelection"
    :allowed-statuses="activeGiftCardStatuses"
    :require-positive-balance="true"
    :allow-multiple="false"
    :title-override="$t($ts.appointment.giftCards.add).toString()"
    :info-message="$t($ts.appointment.giftCards.selectionHelp).toString()"
    @confirm="onGiftCardSelection"
  />

  <gift-card-redeem-dialog
    v-if="giftCardFeatureEnabled"
    v-model="showGiftCardRedeemDialog"
    :initial-code="redeemDialogInitialCode"
    :max-amount="redeemDialogMaxAmount"
    @confirm="onGiftCardRedeemConfirm"
  />
</div>
</template>

<style lang="scss" scoped>
.appointment-card {
  border-radius: 8px;
}

.status-card,
.time-card,
.customer-card,
.type-card,
.price-card,
.notes-card,
.created-card,
.event-card,
.gift-card-card {
  border-radius: 4px;
  transition: all 0.3s;
}

.gift-card-chip-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.gift-card-tooltip {
  white-space: pre-line;
  display: block;
}

.status-card:hover,
.time-card:hover,
.customer-card:hover,
.type-card:hover,
.price-card:hover,
.notes-card:hover,
.created-card:hover,
.event-card:hover,
.gift-card-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.headline {
  font-weight: 500;
  font-size: 1.5rem;
}

.subtitle-1 {
  font-weight: 500;
}

.v-card-title {
  word-break: break-word;
}

.w-100 {
  width: 100%;
}
</style>
